import { MonthlyTopupScheduleComponent } from '../monthly-topup-schedule/monthly-topup-schedule.component';
import { Location } from '@angular/common';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BrandsService } from 'src/app/services/brands/brands.service';
import { SessionService } from '../../services/session/session.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { Plan, StripeService } from '../../services/stripe/stripe.service';
import {
  Category,
  FundMonthlyAllowanceCandidatesResponse,
  MonthlyAllowanceFundingMode,
  PreviewMonthlyAllowanceFundingResponseData,
  TeamsService,
} from '../../services/teams/teams.service';
import { UiAlertService } from '../../services/ui-alert/ui-alert.service';
import { UsersService } from '../../services/users/users.service';
import { TeamCompanyUsersService } from '../../services/teamcompanyusers/teamcompanyusers.service';
import { SnackBarComponent } from '../snackbar/snackbar.component';
import {
  AllowanceFundingAmountDialogComponent,
  AllowanceFundingAmountDialogResult,
} from '../modal/allowancefundingamountdialog/allowance-funding-amount-dialog.component';

@Component({
  selector: 'app-teamedit',
  templateUrl: './teamedit.component.html',
  styleUrls: ['./teamedit.component.scss'],
})
export class TeameditComponent implements OnInit {
  @ViewChild(MonthlyTopupScheduleComponent) monthlyTopupSchedule?: MonthlyTopupScheduleComponent;
  private readonly broadExcludedTransactionTermSet = new Set(['pay', 'prime', 'store']);
  public team: any;
  public members: Array<any> = [];
  public membersLoading = false;
  public membersLoadError = false;
  public action = ''; // new or edit
  public newMembers: Array<any> = [];
  public freePlan = Plan.Free;
  public categories: any;
  public isAdmin = this.usersService.isAdmin();
  public paymentMethod: any;
  showFundMembersWarningBox = false;
  showReimbursementsSpinner = false;
  showMonthlyAllowanceSpinner = false;
  proRateParam = false;
  isTeamAgentSession = false;
  canManagePlanAndFunding = false;
  excludedTransactionTerms: string[] = [];
  includedTransactionTerms: string[] = [];
  isExcludedTransactionsSaving = false;
  isIncludedTransactionsSaving = false;
  readonly excludedTransactionSeparatorKeysCodes = [ENTER, COMMA] as const;
  readonly includedTransactionSeparatorKeysCodes = [ENTER, COMMA] as const;
  @ViewChild('reimbursementsToggle') reimbursementsToggle!: MatSlideToggle;
  @ViewChild('monthlyAllowanceToggle') monthlyAllowanceToggle!: MatSlideToggle;
  @ViewChild('proRateFundingToggle') proRateToggle!: MatSlideToggle;

  constructor(
    private teamsService: TeamsService,
    private sessionService: SessionService,
    private stripeService: StripeService,
    private route: ActivatedRoute,
    private location: Location,
    private uiAlertService: UiAlertService,
    private router: Router,
    private snackBar: MatSnackBar,
    private spinnerService: SpinnerService,
    private usersService: UsersService,
    private teamCompanyUsersService: TeamCompanyUsersService,
    private readonly brandsService: BrandsService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.isTeamAgentSession = this.sessionService.isTeamAgentSession();
    this.teamCompanyUsersService.getManagers().then((roles: any[]) => {
      this.isTeamAgentSession = this.isTeamAgentSession || this.hasOnlyTeamAgentRoles(roles);
      this.canManagePlanAndFunding = !this.isTeamAgentSession;
    });
    this.teamsService.wellspaceChangedObservable.subscribe((wellspace: any): void => {
      if (wellspace) {
        this.team = wellspace;
        this.proRateParam = this.team.proRate === 1;
        this.hydrateExcludedTransactionTerms();
        this.hydrateIncludedTransactionTerms();
        this.getMembers();
      } else if (wellspace === null) {
        this.team = undefined;
        this.members = [];
      }
      if (this.reimbursementsToggle && this.monthlyAllowanceToggle) {
        this.resetAllToggles();
      }
    });
  }

  getTeamId() {
    if (this.team) {
      return this.team.id;
    }
    return '';
  }

  get pendingInvitesCount(): number {
    return this.members.filter((member) => this.needsInvite(member)).length;
  }

  get hasPendingInviteAutomation(): boolean {
    return this.hasPendingAutomation(this.team?.metadata?.inviteAutomation);
  }

  get selectedCategoryCount(): number {
    return Array.isArray(this.team?.categories) ? this.team.categories.length : 0;
  }

  get approvalStatusLabel(): string {
    return this.team?.approved ? 'Approved' : 'Awaiting approval';
  }

  get reimbursementsStatusLabel(): string {
    return this.team?.disabled ? 'Paused' : 'Enabled';
  }

  get monthlyAllowanceStatusLabel(): string {
    return this.team?.topupDisabled ? 'Paused' : 'Enabled';
  }

  get canShowMemberFundingWarning(): boolean {
    return this.showFundMembersWarningBox
      && this.newMembers.length > 0
      && Number(this.team?.amount || 0) > 0
      && !this.paymentMethod?.bankInfo?.isPending;
  }

  private needsInvite(member: any): boolean {
    return !!(member?.memberId || member?.id)
      && !!(member?.userEmail || member?.email)
      && !member?.inviteSentAt
      && !member?.inviteAcceptedAt;
  }

  private hasPendingAutomation(automation: any): boolean {
    return !!automation?.enabled && (
      !!automation?.initialInviteSendAt ||
      !!automation?.nextRetryAt ||
      Number(automation?.pendingInitialCount || 0) > 0 ||
      Number(automation?.pendingRetryCount || 0) > 0
    );
  }

  private hasOnlyTeamAgentRoles(roles: any[]): boolean {
    return Array.isArray(roles)
      && roles.length > 0
      && roles.every((role: any) => role?.role === 'teamagent');
  }

  getMembers(): void {
    this.showFundMembersWarningBox = false;
    this.newMembers = [];
    this.membersLoading = true;
    this.membersLoadError = false;
    this.teamsService.getPaymentMethod(this.team).then((method) => {
      this.teamsService.getPaymentMethods(this.team.id).then((methods) => {
        this.paymentMethod = methods.find(
          (thisMethod: { stripePaymentMethod: string }) =>
            thisMethod.stripePaymentMethod === method,
        );
      });
    });
    const membersRequest = Number(this.team?.amount || 0) > 0
      ? this.teamsService.getTeamMembersForAllowanceFunding(this.team.id)
      : this.teamsService.getTeamMembers(this.team.id, true);
    membersRequest
      .then((members) => {
        this.membersLoading = false;
        this.newMembers = this.getAllowanceFundingMemberEmails(members);
        this.members = members;
        this.members.forEach((member) => {
          if (member.userId === this.team.ownerUserId) {
            member.role = 'team admin';
          }
        });
        this.showFundMembersWarningBox = this.newMembers.length > 0;
        this.teamsService.getCategories().then((categories) => {
          this.categories = categories.map((category) => ({
            taxable: category.taxable,
            toolTip: category.toolTip,
            category: category.name,
            selected: false,
          }));
          this.categories = this.categories.filter((category: Category) =>
            this.team.categories.find((teamCategory: any) => teamCategory === category.category),
          );
        });
      })
      .catch((err) => {
        console.error('Error in get team agency-members', err);
        this.membersLoading = false;
        this.membersLoadError = true;
        this.members = [];
        this.newMembers = [];
        this.showFundMembersWarningBox = false;
      });
  }

  async fundAllowancesClicked(): Promise<void> {
    if (this.monthlyTopupSchedule?.fundingBlocked) return;
    const fundingMode = await this.promptForInitialFundingMode();
    if (fundingMode === null) {
      return;
    }

    this.spinnerService.show();
    let preview: PreviewMonthlyAllowanceFundingResponseData;
    try {
      const previewResponse = await this.teamsService.previewMonthlyAllowanceFunding(
        this.team.id,
        { fundingMode },
      );
      if (!previewResponse.success) {
        throw new Error(previewResponse.message || 'Unable to preview monthly allowance funding.');
      }
      preview = previewResponse.data;
    } catch (err: any) {
      this.handleMonthlyAllowanceFundingError(err);
      return;
    } finally {
      this.spinnerService.hide();
    }

    const confirm = await this.uiAlertService.presentAlertConfirm(
      this.buildFundAllowanceAlertMessage(preview),
    );
    if (!confirm) {
      return;
    }

    this.spinnerService.show();
    try {
      await this.teamsService.fundMonthlyAllowanceCandidates(this.team.id, {
        fundingMode,
        quoteToken: preview.quoteToken,
      });
      this.hideSpinnerAndShowMessage(
        'Payment initiated. Subscription will be activated when the payment completes. Bank payments may take a few days.',
      );
      this.teamsService.setWellspace(this.team);
      setTimeout(() => this.getMembers(), 3000);
    } catch (err: any) {
      this.handleMonthlyAllowanceFundingError(err);
    }
  }

  canSendOneTimeRewards() {
    const brandConfig = this.brandsService.getConfigJSON();
    // TODO: When getConfigJSON is fixed, remove comment
    const brandHaveAccounts = true;// !!brandConfig.content?.ui?.tabs?.accounts;
    return this.team.approved && brandHaveAccounts;
  }

  private buildFundAllowanceAlertMessage(
    preview: PreviewMonthlyAllowanceFundingResponseData,
  ): string {
    const memberCount = preview.candidateCount;
    const invoiceType = this.team.invoiceEmail ? 'invoice' : 'charge';
    const message: string[] = [];
    message.push(`${memberCount} new member${memberCount !== 1 ? 's' : ''}.<br><br>`);
    message.push(
      `This will initiate a monthly allowance recurring ${invoiceType} of ${this.formatCents(
        preview.recurringChargeTotalCents,
      )} to your default payment method on day ${preview.monthlyTopupDay} of each month, with the next full payment on ${new Date(preview.nextFundingDate).toLocaleDateString('en-US', { timeZone: 'UTC' })} (UTC).`,
    );
    if (preview.recurringServiceFeeCents > 0) {
      message.push(
        `This includes a monthly ${this.formatCents(preview.recurringServiceFeeCents)} service fee.`,
      );
    }
    if (this.proRateParam && preview.immediateFundingSubtotalCents > 0) {
      message.push(
        `Initial funding of ${this.formatCents(preview.immediateChargeTotalCents)} will be charged immediately. Prorated funding covers today through the day before the next scheduled top-up.`,
      );
      if (preview.immediateServiceFeeCents > 0) {
        message.push(
          `This includes an initial ${this.formatCents(preview.immediateServiceFeeCents)} service fee.`,
        );
      }
    }

    const premiumMessage = this.getFundingPremiumMessage(memberCount);
    if (premiumMessage) {
      message.push(premiumMessage);
    }
    message.push('Any available credit balance will be applied first before charging the remaining amount.');
    return message.join(' ');
  }

  private getFundingPremiumMessage(memberCount: number): string {
    if (this.team.plan === Plan.Plus && !TeamsService.companyPlusPlan) {
      const premiumAmount = this.stripeService.PREMIUM_MONTHLY_FEE * memberCount;
      return `A Plus Monthly plan recurring monthly charge of $${premiumAmount.toFixed(
        2,
      )} will also be initiated and pro-rated for the initial period.`;
    }
    if (this.team.plan === Plan.PlusAnnual) {
      const premiumAmount = this.stripeService.PREMIUM_ANNUAL_FEE * memberCount;
      return `A Plus Annual plan recurring annual charge of $${premiumAmount.toFixed(
        2,
      )} will also be initiated and pro-rated for the initial period.`;
    }
    return '';
  }

  private async promptForInitialFundingMode(): Promise<
    MonthlyAllowanceFundingMode | null
  > {
    if (!this.proRateParam) {
      return 'none';
    }

    const dialogRef = this.dialog.open(AllowanceFundingAmountDialogComponent, {
      width: '520px',
      data: {
        memberCount: this.newMembers.length,
        monthlyAmount: Number(this.team?.amount || 0),
      },
    });
    const result = (await dialogRef.afterClosed().toPromise()) as
      | AllowanceFundingAmountDialogResult
      | null;

    return result?.fundingMode ?? null;
  }

  private isConflictError(error: any): boolean {
    return error?.status === 409
      || error?.statusCode === 409
      || error?.data?.status === 409
      || error?.data?.statusCode === 409;
  }

  private formatCents(cents: number): string {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  private handleMonthlyAllowanceFundingError(error: any): void {
    if (this.isConflictError(error)) {
      this.hideSpinnerAndShowMessage(
        'The funding details changed. Reloading the team members so you can review a new quote.',
      );
      this.getMembers();
      return;
    }

    console.error('error in monthly allowance funding', error);
    this.hideSpinnerAndShowMessage(
      error?.data?.message || error?.message || 'Unable to fund member allowances.',
    );
  }

  hideSpinnerAndShowMessage(message: string): void {
    this.spinnerService.hide();
    this.snackBar.openFromComponent(SnackBarComponent, { data: message });
  }

  sendEmailsClicked(): void {}

  editName(): void {
    this.uiAlertService
      .presentFieldInput('Change Wellspace Name', 'Wellspace Name', this.team.name)
      .then((result) => {
        if (result) {
          this.teamsService.modifyTeam(this.team.id, { name: result }).then((_) => {
            this.team.name = result;
            this.teamsService.setWellspace(this.team);
            this.snackBar.openFromComponent(SnackBarComponent, { data: 'Wellspace name changed' });
          });
        }
      });
  }

  editCategories(): void {
    this.router.navigate(['managewellspace/categories', this.team.id]);
  }

  get broadExcludedTransactionTerms(): string[] {
    return this.excludedTransactionTerms.filter((term) =>
      this.broadExcludedTransactionTermSet.has(term.trim().toLowerCase())
    );
  }

  async addExcludedTransactionTerm(event: MatChipInputEvent): Promise<void> {
    const previousTerms = [...this.excludedTransactionTerms];
    const nextTerms = this.normalizeExcludedTransactionTerms(previousTerms.concat(event.value));
    event.chipInput?.clear();

    if (JSON.stringify(previousTerms) === JSON.stringify(nextTerms)) {
      return;
    }

    if (!this.validateExcludedTransactionTerms(nextTerms)) {
      return;
    }

    this.excludedTransactionTerms = nextTerms;
    await this.persistExcludedTransactionTerms(previousTerms);
  }

  async addIncludedTransactionTerm(event: MatChipInputEvent): Promise<void> {
    const previousTerms = [...this.includedTransactionTerms];
    const nextTerms = this.normalizeIncludedTransactionTerms(previousTerms.concat(event.value));
    event.chipInput?.clear();

    if (JSON.stringify(previousTerms) === JSON.stringify(nextTerms)) {
      return;
    }

    if (!this.validateIncludedTransactionTerms(nextTerms)) {
      return;
    }

    this.includedTransactionTerms = nextTerms;
    await this.persistIncludedTransactionTerms(previousTerms);
  }

  async removeExcludedTransactionTerm(term: string): Promise<void> {
    const previousTerms = [...this.excludedTransactionTerms];
    const normalizedTerm = `${term || ''}`.trim().toLowerCase();
    this.excludedTransactionTerms = this.excludedTransactionTerms
      .filter((item) => item.trim().toLowerCase() !== normalizedTerm);

    if (JSON.stringify(previousTerms) === JSON.stringify(this.excludedTransactionTerms)) {
      return;
    }

    await this.persistExcludedTransactionTerms(previousTerms);
  }

  async removeIncludedTransactionTerm(term: string): Promise<void> {
    const previousTerms = [...this.includedTransactionTerms];
    const normalizedTerm = `${term || ''}`.trim().toLowerCase();
    this.includedTransactionTerms = this.includedTransactionTerms
      .filter((item) => item.trim().toLowerCase() !== normalizedTerm);

    if (JSON.stringify(previousTerms) === JSON.stringify(this.includedTransactionTerms)) {
      return;
    }

    await this.persistIncludedTransactionTerms(previousTerms);
  }

  trackExcludedTransactionTerm(index: number, term: string): string {
    return `${term}`.trim().toLowerCase();
  }

  trackIncludedTransactionTerm(index: number, term: string): string {
    return `${term}`.trim().toLowerCase();
  }

  editMembers(): void {
    this.router.navigate(['managewellspace/agency-members', this.team.id]);
  }

  deleteClicked(): void {
    const remainingAllowances =
      this.members.length === 0
        ? 0
        : this.members
            .map((member) => parseFloat(member.balance))
            .reduce((accumulator, currentValue) => accumulator + currentValue);
    const info =
      remainingAllowances > 0
        ? `This team's members have a combined remaining allowance of $${remainingAllowances.toFixed(
            2,
          )} which will be refunded through Stripe against eligible prior charges.  `
        : '';
    this.uiAlertService
      .presentAlertConfirm(
        `${info}Do you really want to remove this wellspace${
          this.members.length === 0 ? '' : ' and all its team agency-members'
        }?`,
      )
      .then(async (confirm) => {
        if (confirm) {
          this.spinnerService.show('removing team...');
          try {
            if (remainingAllowances) {
              const customerId = await this.teamsService.getCustomerId(this.team);
              await this.stripeService.issueRefund(customerId, remainingAllowances);
            }
            await this.teamsService.deleteTeam(this.team.id);
            this.snackBar.openFromComponent(SnackBarComponent, { data: 'Team removed' });
            this.teamsService.clearSelectedWellspace();
            this.router.navigate(['']).then(() => window.location.reload());
          } catch (error: any) {
            this.spinnerService.hide();
            this.snackBar.openFromComponent(SnackBarComponent, {
              data: error?.data?.message || error?.message || error,
            });
          }
        }
      });
  }

  async memberDeleted(member: any, dontDelete: boolean): Promise<void> {
    if (member.balance > 0) {
      this.spinnerService.show('Refunding allowance...');
      try {
        const customerId = await this.teamsService.getCustomerId(this.team);
        await this.stripeService.issueRefund(customerId, member.balance);
        if (dontDelete) {
          await this.teamsService.modifyTeamMember(this.team.id, member.memberId, { balance: 0 });
        }
        this.getMembers();
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: `This team member has a remaining balance of $${Number(member.balance).toFixed(
            2,
          )} which will be refunded through Stripe against eligible prior charges.`,
        });
      } catch (error: any) {
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: error?.data?.message || error?.message || 'Unable to refund allowance',
        });
      } finally {
        this.spinnerService.hide();
      }
    } else {
      this.getMembers();
    }
  }

  addPaymentMethod(): void {
    this.router.navigate(['/billing']);
  }

  changePaymentMethod(): void {
    this.router.navigate(['/billing']);
  }

  memberClicked(member: any): void {
    // this.router.navigate(['team-member-detail', this.team.id, member.id]);
  }

  changePlan(): void {
    this.router.navigate(['/managewellspace/plan', this.team.id]);
  }

  private updateMembers(): void {
    this.members = JSON.parse(JSON.stringify(this.members));
  }

  private hydrateExcludedTransactionTerms(): void {
    this.excludedTransactionTerms = this.normalizeExcludedTransactionTerms(
      this.team?.metadata?.excludedTransactions
    );
  }

  private hydrateIncludedTransactionTerms(): void {
    this.includedTransactionTerms = this.normalizeIncludedTransactionTerms(
      this.team?.metadata?.includedTransactions
    );
  }

  private getTransactionTermsUpdateMetadata(): any {
    const metadata = { ...(this.team?.metadata || {}) };
    // PUT /teams preserves stored allowance eligibility when omitted. Resending
    // it would validate an unrelated product edit for this transaction change.
    delete metadata.allowanceEligibleProductIds;
    return metadata;
  }

  private transactionTermsSaveError(error: unknown, message: string): string {
    const response = error as {
      data?: { error?: unknown; message?: unknown };
      error?: { error?: unknown; message?: unknown };
      message?: unknown;
    } | null;
    const detail = [response?.data?.error, response?.data?.message,
      response?.error?.error, response?.error?.message, response?.message]
      .find((value): value is string => typeof value === 'string' && value.trim().length > 0);
    return detail ? `${message}: ${detail}` : message;
  }

  private async persistExcludedTransactionTerms(previousTerms: string[]): Promise<void> {
    if (!this.team?.id || this.isExcludedTransactionsSaving) {
      this.excludedTransactionTerms = previousTerms;
      return;
    }

    this.isExcludedTransactionsSaving = true;
    const metadata = {
      ...this.getTransactionTermsUpdateMetadata(),
      excludedTransactions: this.normalizeExcludedTransactionTerms(this.excludedTransactionTerms),
    };

    try {
      await this.teamsService.modifyTeam(this.team.id, { metadata });
      this.team = {
        ...this.team,
        metadata: { ...(this.team?.metadata || {}), ...metadata },
      };
      this.excludedTransactionTerms = [...metadata.excludedTransactions];
      this.teamsService.setWellspace(this.team);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Excluded transaction names updated',
      });
    } catch (error) {
      this.excludedTransactionTerms = previousTerms;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: this.transactionTermsSaveError(error, 'Unable to update excluded transaction names'),
      });
    } finally {
      this.isExcludedTransactionsSaving = false;
    }
  }

  private async persistIncludedTransactionTerms(previousTerms: string[]): Promise<void> {
    if (!this.team?.id || this.isIncludedTransactionsSaving) {
      this.includedTransactionTerms = previousTerms;
      return;
    }

    this.isIncludedTransactionsSaving = true;
    const metadata = {
      ...this.getTransactionTermsUpdateMetadata(),
      includedTransactions: this.normalizeIncludedTransactionTerms(this.includedTransactionTerms),
    };

    try {
      await this.teamsService.modifyTeam(this.team.id, { metadata });
      this.team = {
        ...this.team,
        metadata: { ...(this.team?.metadata || {}), ...metadata },
      };
      this.includedTransactionTerms = [...metadata.includedTransactions];
      this.teamsService.setWellspace(this.team);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Included transaction names updated',
      });
    } catch (error) {
      this.includedTransactionTerms = previousTerms;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: this.transactionTermsSaveError(error, 'Unable to update included transaction names'),
      });
    } finally {
      this.isIncludedTransactionsSaving = false;
    }
  }

  private validateExcludedTransactionTerms(terms: string[]): boolean {
    const invalidTerm = terms
      .map((term) => `${term || ''}`.trim())
      .find((term) => term.replace(/\s/g, '').length < 2);
    if (!invalidTerm) {
      return true;
    }

    this.snackBar.openFromComponent(SnackBarComponent, {
      data: 'Excluded transaction names must have at least two non-space characters.',
    });
    return false;
  }

  private validateIncludedTransactionTerms(terms: string[]): boolean {
    const invalidTerm = terms
      .map((term) => `${term || ''}`.trim())
      .find((term) => term.replace(/\s/g, '').length < 2);
    if (!invalidTerm) {
      return true;
    }

    this.snackBar.openFromComponent(SnackBarComponent, {
      data: 'Included transaction names must have at least two non-space characters.',
    });
    return false;
  }

  private normalizeExcludedTransactionTerms(value: any): string[] {
    return this.normalizeTransactionTerms(value);
  }

  private normalizeIncludedTransactionTerms(value: any): string[] {
    return this.normalizeTransactionTerms(value);
  }

  private normalizeTransactionTerms(value: any): string[] {
    const rawTerms = this.toTransactionTermList(value);
    const seen = new Set<string>();
    const normalizedTerms: string[] = [];

    rawTerms.forEach((rawTerm) => {
      const term = `${rawTerm || ''}`.trim();
      const normalizedTerm = term.toLowerCase();
      if (!term || seen.has(normalizedTerm)) {
        return;
      }
      seen.add(normalizedTerm);
      normalizedTerms.push(term);
    });

    return normalizedTerms;
  }

  private toTransactionTermList(value: any): any[] {
    if (Array.isArray(value)) {
      return value.reduce((terms: any[], item: any) => {
        return typeof item === 'string' ? terms.concat(item.split(',')) : terms.concat(item);
      }, []);
    }
    if (typeof value === 'string') {
      return value.split(',');
    }
    if (!value || typeof value !== 'object') {
      return [];
    }

    return Object.keys(value).reduce((terms: any[], key: string) => {
      const item = value[key];
      if (Array.isArray(item)) {
        return terms.concat(this.toTransactionTermList(item));
      }
      if (typeof item === 'string') {
        terms.push(...item.split(','));
      } else if (item === true) {
        terms.push(key);
      }
      return terms;
    }, []);
  }

  sendOnetimeClicked(): void {
    this.uiAlertService.teamOneTimeReward(false).then((_) => {
      this.updateMembers();
    });
  }

  sendOnetimeP2pClicked(): void {
    this.uiAlertService.teamOneTimeReward(true).then((_) => {
      this.updateMembers();
    });
  }

  uploadRewardsClicked(): void {
    this.uiAlertService.uploadRewards(this.team.id).then((_) => {
      this.updateMembers();
    });
  }

  resetAllToggles(): void {
    this.reimbursementsToggle.disabled = false;
    this.monthlyAllowanceToggle.disabled = false;
    this.showReimbursementsSpinner = false;
    this.showMonthlyAllowanceSpinner = false;
  }

  enabledChanged(event: MatSlideToggleChange): void {
    const modalMsg = {
      enable:
        'Enabling the allowance reimbursement feature will allow your team agency-members to approve and receive reimbursements for their purchase transactions. Are you sure you want to enable this feature?',
      disable:
        'Disabling the allowance reimbursement feature means your team agency-members can no longer request or approve reimbursements for their eligible purchases. Are you sure you want to proceed?',
    };
    this.uiAlertService
      .presentAlertConfirm(event.checked ? modalMsg.enable : modalMsg.disable)
      .then((confirm: boolean): void => {
        if (confirm) {
          this.reimbursementsToggle.disabled = true;
          this.showReimbursementsSpinner = true;
          this.teamsService
            .modifyTeam(this.team.id, { disabled: !event.checked })
            .then((_) => {
              this.teamsService.setWellspace(this.team);
            })
            .catch((err) => {
              console.error('error in modifyTeam: ', err);
              this.resetAndEnableTeamToggle(this.reimbursementsToggle, event);
              this.resetAllToggles();
            });
        } else {
          this.resetAndEnableTeamToggle(this.reimbursementsToggle, event);
        }
      });
  }

  topupEnabledChanged(event: any): void {
    const modalMsg = {
      enable:
        "Enabling the monthly allowance feature will fund your team's Allowance Balances, and your selected payment method will be charged the monthly allowance amount. Are you sure you want to enable the monthly allowance?",
      disable:
        'Disabling the monthly allowance feature means you will no longer contribute funds to your team agency-members, and you will not be charged for the monthly allowance. Are you sure you want to disable it? ',
    };
    this.uiAlertService
      .presentAlertConfirm(event.checked ? modalMsg.enable : modalMsg.disable)
      .then((confirm: boolean): void => {
        if (confirm) {
          this.monthlyAllowanceToggle.disabled = true;
          this.showMonthlyAllowanceSpinner = true;
          this.teamsService
            .modifyTeam(this.team.id, { topupDisabled: !event.checked })
            .then((response) => {
              if (response && response.errors && response.errors.length > 0) {
                // Handle 207 response with errors
                response.errors.forEach((error: string) => {
                  this.snackBar.openFromComponent(SnackBarComponent, { data: error });
                });
                this.resetAndEnableTeamToggle(this.monthlyAllowanceToggle, event);
                this.resetAllToggles();
              } else {
                this.teamsService.setWellspace(this.team);
              }
            })
            .catch((err) => {
              console.error('error in modifyTeam: ', err);
              this.resetAndEnableTeamToggle(this.monthlyAllowanceToggle, event);
              this.resetAllToggles();
            });
        } else {
          this.resetAndEnableTeamToggle(this.monthlyAllowanceToggle, event);
        }
      });
  }

  private resetAndEnableTeamToggle(toggle: MatSlideToggle, event: any): void {
    toggle.checked = !event.checked;
    toggle.disabled = false;
  }

  privacyChanged(event: any): void {
    this.teamsService.modifyTeam(this.team.id, { transactionPrivacy: event.checked }).then((_) => {
      this.teamsService.setWellspace(undefined);
    });
  }

  private getAllowanceFundingMemberEmails(members: any[]): string[] {
    return members
      .filter((member) => member?.monthlyAllowanceFunding?.candidate === true)
      .map((member) => `${member?.userEmail ?? ''}`.trim())
      .filter((email) => !!email);
  }

  loadTeamMembers(): void {
    console.log('Loading team members');
    this.newMembers = [];
    this.showFundMembersWarningBox = false;
    this.membersLoadError = false;
    const membersRequest = Number(this.team?.amount || 0) > 0
      ? this.teamsService.getTeamMembersForAllowanceFunding(this.team.id)
      : this.teamsService.getTeamMembers(this.team.id, true);
    membersRequest.then((members) => {
      console.log(members);
      this.newMembers = this.getAllowanceFundingMemberEmails(members);
      this.members = members;
      this.showFundMembersWarningBox = this.newMembers.length > 0;
    }).catch((error) => {
      console.error('Error in get team agency-members', error);
      this.members = [];
      this.newMembers = [];
      this.showFundMembersWarningBox = false;
      this.membersLoadError = true;
    });
  }

  proRateToggleChange(event: MatSlideToggleChange): void {
    const modalMsg = {
      enable: 'Enable start funding at the next scheduled top-up?',
      disable: 'Disable start funding at the next scheduled top-up?',
    };
    this.uiAlertService
      .presentAlertConfirm(event.checked ? modalMsg.enable : modalMsg.disable)
      .then(async (confirm: boolean): Promise<void> => {
        if (confirm) {
          this.spinnerService.show('Updating team configurations...');
          this.proRateParam = !event.checked;
          try {
            this.teamsService.updateTeamProRate(this.team.id, this.proRateParam).then(async (_) => {
              const teamInformation = await this.teamsService.getTeamInfo(this.team.id);
              this.team.proRate = teamInformation.proRate;
              this.teamsService.setWellspace(this.team);
              this.spinnerService.hide();
            });
          } catch (error) {
            this.proRateToggle.checked = !event.checked;
            this.spinnerService.hide();
          }
        } else {
          this.proRateToggle.checked = !event.checked;
          this.spinnerService.hide();
        }
      });
  }

  public showWarnCard() {
    if (!this.team.proRate && !this.team.topupDisabled) {
      return true;
    }
    return false;
  }
}
